import { useQueries, useQuery } from '@tanstack/react-query';

import { captureException } from '../../logger/Logger';
import { useMastraClient } from '../providers/MastraClient';
import type { ExperimentDetail, ExperimentSummary } from './loadExperiments';
import {
    attachExperimentScores,
    judgeScoreValues,
    loadExperiment,
    loadExperiments,
    loadExperimentScores,
} from './loadExperiments';

const isInProgress = (status: ExperimentSummary['status']): boolean =>
    status === 'running' || status === 'pending';

/**
 * One query per run. A single batch hid every rubric until the slowest
 * score request finished, so the list looked unscored until that batch
 * landed in the cache — often only after opening a run and coming back.
 */
export const useExperimentRubricScores = (
    agentId: string,
    runs: readonly ExperimentSummary[],
): {
    scoresByRunId: ReadonlyMap<string, number[]>;
    loadingRunIds: ReadonlySet<string>;
    isError: boolean;
} => {
    const client = useMastraClient();
    const results = useQueries({
        queries: runs.map((run) => ({
            queryKey: ['experimentScores', agentId, run.id],
            queryFn: async () => {
                try {
                    return judgeScoreValues(
                        await loadExperimentScores(client, run.id),
                    );
                } catch (error) {
                    captureException(error);
                    throw error;
                }
            },
            enabled: agentId !== '' && run.id !== '',
            staleTime: isInProgress(run.status) ? 0 : Number.POSITIVE_INFINITY,
            refetchInterval: isInProgress(run.status) ? 5000 : false,
        })),
    });

    const scoresByRunId = new Map<string, number[]>();
    const loadingRunIds = new Set<string>();
    runs.forEach((run, index) => {
        const result = results[index];
        if (result?.isSuccess) {
            scoresByRunId.set(run.id, result.data);
            return;
        }
        if (result == null || result.isPending) {
            loadingRunIds.add(run.id);
        }
    });

    return {
        scoresByRunId,
        loadingRunIds,
        isError: results.some((result) => result.isError),
    };
};

export const useExperiments = (
    agentId: string,
): {
    runs: ExperimentSummary[];
    isLoading: boolean;
    isError: boolean;
} => {
    const client = useMastraClient();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['experiments', agentId],
        queryFn: async () => {
            try {
                return await loadExperiments(client, agentId);
            } catch (error) {
                captureException(error);
                throw error;
            }
        },
        enabled: agentId !== '',
        refetchInterval: 5000,
    });

    return {
        runs: data ?? [],
        isLoading,
        isError,
    };
};

export const useExperiment = (
    agentId: string,
    experimentId: string,
): {
    run: ExperimentDetail | null;
    isLoading: boolean;
    isError: boolean;
} => {
    const client = useMastraClient();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['experiment', agentId, experimentId],
        queryFn: async () => {
            try {
                const detail = await loadExperiment(
                    client,
                    agentId,
                    experimentId,
                );
                if (detail == null) {
                    return null;
                }

                try {
                    const scores = await loadExperimentScores(
                        client,
                        experimentId,
                    );
                    return attachExperimentScores(detail, scores);
                } catch (error) {
                    captureException(error);
                    return detail;
                }
            } catch (error) {
                captureException(error);
                throw error;
            }
        },
        enabled: agentId !== '' && experimentId !== '',
        refetchInterval: (query) =>
            query.state.data != null &&
            isInProgress(query.state.data.summary.status)
                ? 5000
                : false,
    });

    return {
        run: data ?? null,
        isLoading,
        isError,
    };
};
